<template>
  <div class="search-bar-container">
    <div class="search-bar">
      <div class="input-wrapper">
        <label
          for="search-input"
          class="search-icon"
        >
          <Icon
            name="lucide:search"
            :aria-label="$t('search.searchSite')"
          />
        </label>
        <form
          class="search-form"
          @submit.prevent="handleSearchSubmit"
        >
          <input
            id="search-input"
            ref="search"
            v-model="searchTerm"
            :placeholder="placeholderText"
            class="search-input"
            type="text"
            spellcheck="false"
            data-testid="search-input-field"
            autocomplete="off"
            @input="handleSearch"
          />
          <button
            type="button"
            class="clear-search-button"
            :aria-label="$t('search.clearSearch')"
            @click="clearSearchInput"
          >
            <Icon
              name="lucide:x"
              size="16"
            />
          </button>
          <button
            type="submit"
            class="search-button"
          >
            {{ $t('search.searchButtonText') }}
          </button>
        </form>
      </div>
    </div>
    <div
      v-if="searchSuggestions.length > 0 && suggestionsOpen"
      ref="suggestionsDropdown"
      class="suggestions-dropdown"
    >
      <div
        v-for="(result, index) in searchSuggestions"
        :key="result.id"
        class="result"
      >
        <a
          :href="link(result)"
          target="_blank"
          rel="noopener noreferrer"
          class="search-link"
          @blur="blurSearchSuggestions(index)"
        >
          <Icon
            :name="result.file_extension ? 'lucide:file' : 'lucide:link'"
            size="20"
          />
          <span>{{ resultType(result) }}</span>
          <span class="result-arrow">
            <Icon
              name="lucide:chevron-right"
              size="20"
            />
          </span>
          <span>{{ resultTitle(result) }}</span>
        </a>
      </div>
    </div>
  </div>
</template>

<script setup>
import { onClickOutside } from '@vueuse/core';
import { ref, useTemplateRef } from 'vue';

const suggestionsDropdown = useTemplateRef('suggestionsDropdown');

const { $t } = useNuxtApp();
const props = defineProps({
  searchSuggestions: {
    type: Array,
    default: () => [],
  },
  initialSearchTerm: {
    type: String,
    default: '',
  },
});
onClickOutside(suggestionsDropdown, closeSuggestionsDropdown);

const searchTerm = ref(props.initialSearchTerm || '');
const placeholderText = ref($t('search.whatCanWeHelpYouFind'));
const emit = defineEmits(['search-term', 'full-search-initiated']);
const suggestionsOpen = ref(true);

function closeSuggestionsDropdown() {
  emit('full-search-initiated', searchTerm.value);
  suggestionsOpen.value = false;
}
function blurSearchSuggestions(index) {
  if (index !== props.searchSuggestions.length - 1) return;
  suggestionsOpen.value = false;
}
function varyPlaceholderText() {
  const placeholderOptions = [
    $t('search.whatCanWeHelpYouFind'),
    $t('search.startTypingForAnswers'),
    $t('search.lookingForSomething'),
  ];
  const randomIndex = Math.floor(Math.random() * placeholderOptions.length);
  return placeholderOptions[randomIndex];
}

function handleSearch() {
  emit('search-term', searchTerm.value);
}

function handleSearchSubmit() {
  emit('full-search-initiated', searchTerm.value);
  placeholderText.value = varyPlaceholderText();
}

function clearSearchInput() {
  searchTerm.value = '';
  placeholderText.value = varyPlaceholderText();
}

function truncateString(str, maxLength = 50, ellipsis = '...') {
  if (typeof str !== 'string' || str.length === 0) return '';
  return str.length > maxLength ? `${str.slice(0, maxLength)}${ellipsis}` : str;
}

function link(result) {
  switch (result.type) {
    case 'live_feed':
      return `${result.link}/live-feed#${result.id}`;
    case 'directory':
      return `${result.link}/staff?=${result.id}`;
    case 'cms_page':
      return `${result.link}/page/${result.slug}`;
    case 'form':
      return `${result.link}/forms/${result.id}`;
    case 'scores_schedule':
      return `${result.link}/athletics#${result.id}`;
    case 'event':
      return `${result.link}/events?=${result.id}`;
    case 'article':
      return `${result.link}/article/${result.id}`;
    case 'public_file':
    case 'documents_asset':
      return `${result.url}`;
    case 'cms_link':
      return `${result.content.url}`;
    default:
      return `${result.link}`;
  }
}

function resultTitle(result) {
  switch (result.type) {
    case 'directory':
      return result.full_name;
    case 'scores_schedule':
      return result.title || `${result.home_team} vs ${result.away_team}`;
    case 'event':
    case 'article':
      return result.title;
    case 'live_feed':
      return truncateString(result.status);
    case 'form':
    case 'public_file':
    case 'documents_asset':
    case 'cms_link':
    case 'cms_page':
      return result.name;
    default:
      return $t('search.fileName');
  }
}

function resultType(result) {
  switch (result.type) {
    case 'directory':
      return $t('search.staffTitle');
    case 'cms_page':
      return $t('search.pageTitle');
    case 'live_feed':
      return $t('search.liveFeedTitle');
    case 'form':
      return $t('search.formTitle');
    case 'scores_schedule':
    case 'event':
      return $t('search.eventTitle');
    case 'article':
      return $t('search.articleTitle');
    case 'cms_link':
      return $t('search.linkTitle');
    default:
      return $t('search.documentTitle');
  }
}
</script>

<style lang="scss" scoped>
.search-bar-container {
  --color-grey-10: #d3d3d3;
  --color-grey-50: #f7f7f7;
  --color-grey-100: #e0e0e0;
  --color-grey-200: #d3d3d3;
  --color-blue-300: #6e91d7;
  --color-blue-500: #1850c1;
  --border-radius-xs: 4px;
  --color-text-disabled: #616161;

  z-index: 10;
  position: relative;
}

.search-bar {
  background: var(--primary-white);
  border-radius: 8px;
  box-sizing: border-box;
  padding: 16px;

  .search-form {
    display: flex;
    align-items: center;
    gap: 16px;
    width: 100%;
  }

  .input-wrapper {
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    padding: 8px 12px;
    gap: 16px;
    border: 1px solid var(--color-grey-200);
    border-radius: var(--border-radius-xs);
    background: var(--color-grey-50);
    width: 100%;
    font-size: 16px;
    &:hover {
      border: 1px var(--color-blue-300) solid;
    }
    &:focus-within {
      border: 1px var(--color-blue-500) solid;
    }

    .search-input {
      display: block;
      border: none;
      background: transparent;
      color: var(--color-text-disabled);
      font-size: 14px;
      font-weight: 450;
      line-height: 16px;
      letter-spacing: 0.4px;
      width: 100%;

      &:focus {
        outline: none;
      }
    }

    .search-icon {
      display: flex;
    }

    .clear-search-button {
      display: flex;
      border: none;
      background: none;
      padding: 0;

      &:hover {
        cursor: pointer;
      }
    }

    .search-button {
      padding: 8px 16px;
      background-color: var(--color-grey-100);
      color: var(--color-text-disabled);
      border-radius: 1000px;
      border-style: none;
      &:focus-visible {
        outline: 2px solid var(--color-blue-500);
        outline-offset: 1px;
      }
      &:hover {
        cursor: pointer;
      }
    }

    /* When input has value, change button color to primary */
    .search-input:not(:placeholder-shown) ~ .search-button {
      background: var(--primary-button-background);
      color: var(--primary-button-text-contrast);
      border-radius: var(--primary-button-border-radius);
      border: var(--primary-button-border);
      &:hover {
        background-color: var(--primary-button-hover);
      }
    }
  }
}

.suggestions-dropdown {
  position: absolute;
  left: 0;
  right: 0;
  background: var(--primary-white);
  border-radius: 8px;
  border: 1px solid var(--color-grey-10);
  box-shadow: 0px -6px 20px 0px rgba(0, 0, 0, 0.06);
  box-sizing: border-box;
  padding: 16px 0px;
  margin-top: -8px;
  margin-left: 16px;
  margin-right: 16px;

  .result {
    display: flex;
    align-items: center;
    padding: 12px 0 12px 16px;
    font-size: 14px;
    font-weight: 450;
    line-height: 20px;
    letter-spacing: 0.6px;

    &:hover {
      background: color-mix(in srgb, var(--primary-button-background) 10%, #fff);
    }

    .search-link {
      display: flex;
      align-items: center;
      text-decoration: none;
      color: #7f7f7f;
      gap: 8px;
      width: 100%;

      .file-type {
        background-repeat: no-repeat;
        background-size: cover;
        height: 20px;
        width: 20px;
      }
      .result-arrow .iconify {
        display: flex;
      }
    }
  }
}
</style>
